import {
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  addEdge,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface InputNodeData {
  label: string
  format: string
  content?: InputFolderNodeConfig | TextInputNodeConfig | FileInputNodeConfig | RecordAudioNodeConfig | CrawlerInputNodeConfig
  execution_status?: {
    error: string | null
    success: boolean
    executed: boolean
    timestamp: string
    executionOrder: number | null
  }
  [key: string]: unknown
}

export interface ProcessingNodeData {
  label: string
  format: string
  content?:
    | DocumentComparisonNodeConfig
    | TextTranslationNodeConfig
    | LLMGenerateNodeConfig
    | FormatTemplateNodeConfig
    | SplitDocumentNodeConfig
    | ExtractContentNodeConfig
    | AudioTranscriptionNodeConfig
    | GenerateReportNodeConfig
  execution_status?: {
    error: string | null
    success: boolean
    executed: boolean
    timestamp: string
    executionOrder: number | null
  }
  [key: string]: unknown
}

export interface OutputNodeData {
  label: string
  format: string
  content?: OutputFolderNodeConfig | RestApiOutputNodeConfig | EmailOutputNodeConfig
  execution_status?: {
    error: string | null
    success: boolean
    executed: boolean
    timestamp: string
    executionOrder: number | null
  }
  [key: string]: unknown
}

export type NodeData = InputNodeData | ProcessingNodeData | OutputNodeData

export type FlowState = {
  nodes: Node<NodeData>[]
  edges: Edge[]
  selectedNode: Node<NodeData> | null
  selectedEdge: Edge | null
  agentId: string | null
  name: string
  version: number
  selectedExecution: AgentExecution | null
  pendingReviews: PendingReview[] | null
  isLoadingPendingReviews: boolean
  isRecordingAudio: boolean
  setIsRecordingAudio: (value: boolean) => void
  shouldOpenValidationPanel: boolean
  setShouldOpenValidationPanel: (value: boolean) => void

  setAgentId: (id: string | null) => void
  setName: (name: string) => void
  setVersion: (version: number) => void

  setNodes: (nodes: Node<NodeData>[]) => void
  setEdges: (edges: Edge[]) => void
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect

  addNode: (node: Node<NodeData>) => void
  updateNode: (nodeId: string, data: Partial<NodeData>) => void
  deleteNode: (nodeId: string) => void

  updateEdge: (edgeId: string, data: Partial<Edge>) => void
  deleteEdge: (edgeId: string) => void

  setSelectedNode: (node: Node<NodeData> | null) => void
  setSelectedEdge: (edge: Edge | null) => void

  setSelectedExecution: (execution: AgentExecution | null) => void
  setPendingReviews: (reviews: PendingReview[] | null) => void
  setIsLoadingPendingReviews: (loading: boolean) => void

  resetFlow: () => void
  saveToLocalStorage: () => void

  loadAgentFromResponse: (response: FlowStateResponse) => void

  flushRecordingAndSave: (() => Promise<void>) | null
  setFlushRecordingAndSave: (fn: (() => Promise<void>) | null) => void

  saveFlowToBackend: (() => Promise<boolean>) | null
  setSaveFlowToBackend: (fn: (() => Promise<boolean>) | null) => void
  reloadFlowState: (() => Promise<void>) | null
  setReloadFlowState: (fn: (() => Promise<void>) | null) => void

  stopRecordingAndWait: (() => Promise<void>) | null
  setStopRecordingAndWait: (fn: (() => Promise<void>) | null) => void
}

const initialNodes: Node<NodeData>[] = []
const initialEdges: Edge[] = []

export const useFlowStore = create<FlowState>()(
  persist(
    (set, get) => ({
      nodes: initialNodes,
      edges: initialEdges,
      selectedNode: null,
      selectedEdge: null,
      agentId: null,
      name: 'Novo Agente',
      version: 0,
      pendingReviews: null,
      isLoadingPendingReviews: false,
      selectedExecution: null,
      shouldOpenValidationPanel: false,
      setShouldOpenValidationPanel: (value) => set({ shouldOpenValidationPanel: value }),
      flushRecordingAndSave: null,
      setFlushRecordingAndSave: (fn) => set({ flushRecordingAndSave: fn }),

      saveFlowToBackend: null,
      setSaveFlowToBackend: (fn) => set({ saveFlowToBackend: fn }),
      reloadFlowState: null,
      setReloadFlowState: (fn) => set({ reloadFlowState: fn }),

      stopRecordingAndWait: null,
      setStopRecordingAndWait: (fn) => set({ stopRecordingAndWait: fn }),

      isRecordingAudio: false,
      setIsRecordingAudio: (value) => set({ isRecordingAudio: value }),

      setAgentId: (id) => set({ agentId: id }),
      setName: (name) => set({ name }),
      setVersion: (version) => set({ version }),

      setNodes: (nodes) => set({ nodes }),
      setEdges: (edges) => set({ edges }),

      onNodesChange: (changes: NodeChange[]) => {
        const updatedNodes = applyNodeChanges(
          changes,
          get().nodes
        ) as Node<NodeData>[]
        const currentSelectedNode = get().selectedNode
        let updatedSelectedNode = null

        if (currentSelectedNode) {
          updatedSelectedNode =
            updatedNodes.find((node) => node.id === currentSelectedNode.id) ||
            null
        }

        set({
          nodes: updatedNodes,
          selectedNode: updatedSelectedNode,
        })
      },

      onEdgesChange: (changes: EdgeChange[]) => {
        set({
          edges: applyEdgeChanges(changes, get().edges),
          selectedEdge: null,
        })
      },

      onConnect: (connection: Connection) => {
        set({
          edges: addEdge(connection, get().edges),
        })
      },

      addNode: (node) => {
        set({
          nodes: [...get().nodes, node],
        })
      },

      updateNode: (nodeId, data) => {
        const currentSelectedNode = get().selectedNode

        const newNodes = get().nodes.map((node) => {
          if (node.id === nodeId) {
            const updatedNode = {
              ...node,
              data: { ...node.data, ...data } as NodeData,
            }
            return updatedNode
          }
          return node
        })

        let updatedSelectedNode = currentSelectedNode
        if (currentSelectedNode && currentSelectedNode.id === nodeId) {
          updatedSelectedNode =
            newNodes.find((node) => node.id === nodeId) || null
        }

        set({
          nodes: newNodes,
          selectedNode: updatedSelectedNode,
        })
      },

      deleteNode: (nodeId) => {
        set({
          nodes: get().nodes.filter((node) => node.id !== nodeId),
          edges: get().edges.filter(
            (edge) => edge.source !== nodeId && edge.target !== nodeId
          ),
          selectedNode:
            get().selectedNode?.id === nodeId ? null : get().selectedNode,
        })
      },

      updateEdge: (edgeId, data) => {
        set({
          edges: get().edges.map((edge) => {
            if (edge.id === edgeId) {
              return { ...edge, ...data }
            }
            return edge
          }),
        })
      },

      deleteEdge: (edgeId) => {
        set({
          edges: get().edges.filter((edge) => edge.id !== edgeId),
          selectedEdge:
            get().selectedEdge?.id === edgeId ? null : get().selectedEdge,
        })
      },

      setSelectedNode: (node) => {
        set({
          selectedNode: node,
          selectedEdge: null,
        })
      },

      setSelectedEdge: (edge) => {
        set({
          selectedEdge: edge,
          selectedNode: null,
        })
      },

      setSelectedExecution: (execution) =>
        set({ selectedExecution: execution }),

      setPendingReviews: (reviews) => set({ pendingReviews: reviews }),

      setIsLoadingPendingReviews: (loading) =>
        set({ isLoadingPendingReviews: loading }),

      resetFlow: () => {
        set({
          nodes: initialNodes,
          edges: initialEdges,
          selectedNode: null,
          selectedEdge: null,
          version: 0,
          agentId: null,
          name: 'Novo Agente',
          selectedExecution: null,
          pendingReviews: null,
          isLoadingPendingReviews: false,
        })
      },

      saveToLocalStorage: () => {},

      loadAgentFromResponse: (response: FlowStateResponse) => {
        if (response.states && response.states.length > 0) {
          const state = response.states[0]
          set({
            nodes: state.nodes as Node<NodeData>[],
            edges: state.edges,
            selectedNode: state.selectedNode as Node<NodeData> | null,
            selectedEdge: state.selectedEdge,
            version: state.version,
            name: response.name,
            agentId: response.external_id,
            selectedExecution: null,
          })
        }
      },
    }),
    {
      name: 'react-flow-storage',
    }
  )
)
